import React, { useState, useMemo } from 'react'
import { SecurityCard } from '@/components/ui/SecurityCard'
import { CheckCircle } from 'lucide-react'
import { useI18n } from '../../i18n'
import { componentScope } from '../../lib/cssScope'
import { Select } from '@/components/ui/Select'
import { GovernanceListingSection } from '@/components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import localStyles from './PermissionsManagement.module.scss'

interface Permission {
  id: string
  name: string
  category: string
  description?: string
}

interface User {
  id: string
  username: string
  email: string
  role: string
  permissions: string[]
}

interface PermissionsManagementProps {
  users: User[]
  availablePermissions: (Permission | string)[]
  onPermissionChange: (userId: string, permission: string, granted: boolean) => void
}

const mapPermissionToCategory = (permission: string): string => {
  if (permission.startsWith('access.')) return 'access'
  if (permission.startsWith('manage.')) return 'manage'
  if (permission.startsWith('system.')) return 'system'
  if (permission.startsWith('security.')) return 'security'
  return 'other'
}

const permissionCategories = [
  { id: 'access', name: 'Access Control', color: '#3b82f6' },
  { id: 'manage', name: 'User Management', color: '#10b981' },
  { id: 'system', name: 'System Administration', color: '#8b5cf6' },
  { id: 'security', name: 'Security Operations', color: '#f59e0b' },
]

export const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  users,
  availablePermissions,
  onPermissionChange,
}) => {
  const { t } = useI18n()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const normalizedPermissions = useMemo(() => {
    return availablePermissions.map((perm) => {
      if (typeof perm === 'string') {
        return {
          id: perm,
          name: perm.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim() || perm,
          category: mapPermissionToCategory(perm),
          description: perm,
        }
      }
      return perm
    })
  }, [availablePermissions])

  const permissionsByCategory = useMemo(() => {
    const grouped: Record<string, Permission[]> = {}
    normalizedPermissions.forEach((perm) => {
      const category = perm.category || 'other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(perm)
    })
    return grouped
  }, [normalizedPermissions])

  const stats = useMemo(() => {
    const totalUsers = users.length
    const totalPermissions = normalizedPermissions.length
    const usersWithPermissions = users.filter((u) => u.permissions && u.permissions.length > 0).length
    const averagePermissions =
      users.length > 0
        ? Math.round(users.reduce((sum, u) => sum + (u.permissions?.length || 0), 0) / users.length)
        : 0

    return {
      totalUsers,
      totalPermissions,
      usersWithPermissions,
      averagePermissions,
    }
  }, [users, normalizedPermissions])

  const filteredPermissions = useMemo(() => {
    if (selectedCategory === 'all') {
      return normalizedPermissions
    }
    return normalizedPermissions.filter((perm) => perm.category === selectedCategory)
  }, [normalizedPermissions, selectedCategory])

  const getPermissionColor = (permission: string) => {
    if (permission === 'all' || permission.includes('admin')) return '#ef4444'
    if (permission.startsWith('access.')) return '#3b82f6'
    if (permission.startsWith('manage.')) return '#10b981'
    if (permission.startsWith('system.')) return '#8b5cf6'
    if (permission.startsWith('security.')) return '#f59e0b'
    return '#6b7280'
  }

  const hasPermission = (user: User, permission: string) => {
    if (user.permissions?.includes('all') || user.permissions?.includes('admin')) return true
    return user.permissions?.includes(permission) || false
  }

  const togglePermission = (user: User, permission: string) => {
    const granted = hasPermission(user, permission)
    onPermissionChange(user.id, permission, !granted)
  }

  return (
    <div className={`${componentScope('PermissionsManagement')} ${listStyles.pageStack}`}>
      <GovernanceListingSection title={t('admin.perm.metricsTitle', 'Permission overview')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('admin.perm.totalUsers', 'Total users')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{stats.totalUsers}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
            <p className={listStyles.metricTileLabel}>{t('admin.perm.totalPermissions', 'Total permissions')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{stats.totalPermissions}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
            <p className={listStyles.metricTileLabel}>{t('admin.perm.usersWithPermissions', 'Users with permissions')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{stats.usersWithPermissions}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileOrange}`}>
            <p className={listStyles.metricTileLabel}>{t('admin.perm.avgPermissions', 'Avg permissions / user')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textOrange}`}>{stats.averagePermissions}</p>
          </div>
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection title={t('admin.perm.byCategory', 'Coverage by permission category')}>
        <div className={localStyles.donutGrid}>
          {permissionCategories.map((category) => {
            const categoryPermissions = permissionsByCategory[category.id] || []
            const categoryUsers = users.filter((user) =>
              categoryPermissions.some((perm) => hasPermission(user, perm.id))
            ).length
            const categoryPercent =
              users.length > 0 ? Math.round((categoryUsers / users.length) * 100) : 0

            return (
              <div key={category.id} className={localStyles.donutCell}>
                <SecurityCard
                  title={category.name}
                  percent={categoryPercent}
                  color={category.color}
                  size="large"
                  chartSize={108}
                  innerRadius={40}
                  outerRadius={48}
                  statusLabel={t('admin.perm.usersWithAccess', 'Users')}
                  statusText={t('admin.perm.usersCount', '{count} of {total}', {
                    count: categoryUsers,
                    total: users.length,
                  })}
                />
              </div>
            )
          })}
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection
        title={t('admin.perm.userPermissionsTitle', 'User permissions')}
        toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={listStyles.toolbarField}>
                <Select
                  label={t('admin.perm.filterCategory', 'Category')}
                  options={[
                    { value: 'all', label: t('admin.perm.allCategories', 'All categories') },
                    ...permissionCategories.map((cat) => ({ value: cat.id, label: cat.name })),
                  ]}
                  value={selectedCategory}
                  onChange={(v) => {
                    if (v != null) setSelectedCategory(String(v))
                  }}
                  placeholder={t('admin.perm.filterCategory', 'Category')}
                  minWidth="11rem"
                />
              </div>
            </div>
          </div>
        }
      >
        <div className={localStyles.userStack}>
          {users.map((user) => (
            <div
              key={user.id}
              className={localStyles.userRow}
              onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
            >
              <div className={localStyles.userRowHeader}>
                <div className={localStyles.userRowLeft}>
                  <div className={localStyles.avatar}>
                    <span className={localStyles.avatarLetter}>{user.username.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className={localStyles.userMeta}>
                    <div className={localStyles.userName}>{user.username}</div>
                    <div className={localStyles.userEmail}>{user.email}</div>
                  </div>
                </div>
                <div className={localStyles.userRowRight}>
                  <span
                    className={`${listStyles.badge} ${user.role === 'admin' ? listStyles.badgeMissed : listStyles.badgeCompleted}`}
                  >
                    {user.role === 'admin' ? t('admin.admin') : t('admin.user')}
                  </span>
                  <span className={localStyles.permissionCount}>
                    {user.permissions?.length || 0} {t('admin.perm.permissionsShort', 'permissions')}
                  </span>
                </div>
              </div>

              {selectedUser?.id === user.id && (
                <div className={localStyles.expandedBlock}>
                  <div className={localStyles.permissionList}>
                    {filteredPermissions.map((permission) => {
                      const hasPerm = hasPermission(user, permission.id)
                      return (
                        <div
                          key={permission.id}
                          className={localStyles.permissionRow}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className={localStyles.permissionRowLabel}>
                            <input
                              type="checkbox"
                              checked={hasPerm}
                              onChange={() => togglePermission(user, permission.id)}
                              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              disabled={user.role === 'admin' && (permission.id === 'all' || permission.id.includes('admin'))}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="min-w-0 flex-1">
                              <div className={localStyles.permissionNameRow}>
                                <span
                                  className={localStyles.permissionName}
                                  style={{ color: hasPerm ? getPermissionColor(permission.id) : undefined }}
                                >
                                  {permission.name}
                                </span>
                                {hasPerm && <CheckCircle className={listStyles.badgeIcon} aria-hidden style={{ color: 'rgb(22 163 74)' }} />}
                                <span className={localStyles.permissionId}>{permission.id}</span>
                              </div>
                              {permission.description && (
                                <p className={localStyles.permissionDesc}>{permission.description}</p>
                              )}
                            </div>
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </GovernanceListingSection>
    </div>
  )
}

export default PermissionsManagement
